import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getGroup, updateGroup, deleteGroup, updateGroupClients, buildGroupModality, getClients, getGroupSessions, getGroupTelegramLink, disconnectGroupTelegram, getGroupMessages, analyzeGroupChat, getGroupSessionTypes, createGroupSessionType, deleteGroupSessionType } from '../api/client';
import SessionForm from '../components/SessionForm';
import LoadingLeaf from '../components/LoadingLeaf';

export default function GroupDetail() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [group, setGroup] = useState(null);
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [buildingModality, setBuildingModality] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [editingGroup, setEditingGroup] = useState(false);
    const [editForm, setEditForm] = useState({ name: '', description: '' });
    const [groupSessions, setGroupSessions] = useState([]);
    const [showAddDropdown, setShowAddDropdown] = useState(false);
    const [addClientId, setAddClientId] = useState('');
    const [groupTelegram, setGroupTelegram] = useState(null);
    const [groupMessages, setGroupMessages] = useState([]);
    const [analyzingChat, setAnalyzingChat] = useState(false);
    const [chatAnalysis, setChatAnalysis] = useState(null);
    const [activeTab, setActiveTab] = useState('overview');
    const [showAddSession, setShowAddSession] = useState(false);
    const [showModalityNotesModal, setShowModalityNotesModal] = useState(false);
    const [modalityNotes, setModalityNotes] = useState('');
    const [sessionTypes, setSessionTypes] = useState([]);
    const [showAddTypeRow, setShowAddTypeRow] = useState(false);
    const [newTypeName, setNewTypeName] = useState('');

    useEffect(() => { loadGroup(id); getClients().then(res => setClients(res.data || [])).catch(() => {}); }, [id]);

    useEffect(() => {
        if (!group) return;
        const interval = setInterval(() => {
            getGroupMessages(group.id).then(res => setGroupMessages(res.data || []));
        }, 10000);
        return () => clearInterval(interval);
    }, [group?.id]);

    const loadGroup = async (groupId) => {
        setLoading(true);
        setNotFound(false);
        try {
            const [groupRes, sessionsRes, telegramRes, messagesRes, typesRes] = await Promise.all([
                getGroup(groupId),
                getGroupSessions(groupId),
                getGroupTelegramLink(groupId),
                getGroupMessages(groupId),
                getGroupSessionTypes(groupId),
            ]);
            setGroup(groupRes.data);
            setGroupSessions(sessionsRes.data || []);
            setGroupTelegram(telegramRes.data);
            setGroupMessages(messagesRes.data || []);
            setSessionTypes(typesRes.data || []);
            setChatAnalysis(groupRes.data.chat_analysis || null);
            setShowAddDropdown(false);
            setAddClientId('');
        } catch (err) {
            setNotFound(true);
        } finally {
            setLoading(false);
        }
    };

    const handleAnalyzeChat = async () => {
        setAnalyzingChat(true);
        try {
            const res = await analyzeGroupChat(group.id);
            setChatAnalysis(res.data);
            showToast('Chat analyzed');
        } catch (err) {
            setError(err.response?.data?.detail || 'Could not analyze chat.');
        } finally {
            setAnalyzingChat(false);
        }
    };

    const handleDisconnectGroupTelegram = async () => {
        if (!window.confirm('Disconnect this group from Telegram?')) return;
        await disconnectGroupTelegram(group.id);
        setGroupTelegram(prev => ({ ...prev, telegram_connected: false }));
        showToast('Telegram group disconnected');
    };

    const handleUpdateGroup = async () => {
        if (!editForm.name.trim()) return;
        setSaving(true);
        try {
            await updateGroup(group.id, editForm);
            setGroup(prev => ({ ...prev, ...editForm }));
            setEditingGroup(false);
            showToast('Group updated');
        } catch (err) {
            setError('Could not update group.');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteGroup = async () => {
        if (!window.confirm(`Delete "${group.name}"? This cannot be undone.`)) return;
        try {
            await deleteGroup(group.id);
            navigate('/groups');
        } catch (err) {
            setError('Could not delete group.');
        }
    };

    const removeClient = async (clientId) => {
        const newIds = (group.clients || []).map(c => c.id).filter(cid => cid !== clientId);
        try {
            await updateGroupClients(group.id, newIds);
            const updated = (group.clients || []).filter(c => c.id !== clientId);
            setGroup(prev => ({ ...prev, clients: updated }));
            showToast('Client removed');
        } catch (err) {
            setError('Could not remove client.');
        }
    };

    const handleAddClient = async () => {
        if (!addClientId) return;
        const currentIds = (group.clients || []).map(c => c.id);
        const newIds = [...currentIds, addClientId];
        try {
            await updateGroupClients(group.id, newIds);
            const newClient = clients.find(c => c.id === addClientId);
            const updated = [...(group.clients || []), newClient];
            setGroup(prev => ({ ...prev, clients: updated }));
            setAddClientId('');
            setShowAddDropdown(false);
            showToast('Client added');
        } catch (err) {
            setError('Could not add client.');
        }
    };

    const handleAddSessionType = async () => {
        if (!newTypeName.trim()) return;
        try {
            const res = await createGroupSessionType(group.id, newTypeName.trim());
            setSessionTypes(prev => [...prev, res.data]);
            setNewTypeName('');
            setShowAddTypeRow(false);
            showToast('Session type added');
        } catch (err) {
            setError('Could not add session type.');
        }
    };

    const handleRemoveSessionType = async (typeId) => {
        try {
            await deleteGroupSessionType(group.id, typeId);
            setSessionTypes(prev => prev.filter(t => t.id !== typeId));
            showToast('Session type removed');
        } catch (err) {
            setError('Could not remove session type.');
        }
    };

    const handleBuildModality = async (notes) => {
        setShowModalityNotesModal(false);
        setBuildingModality(true);
        try {
            const res = await buildGroupModality(group.id, notes);
            setGroup(prev => ({ ...prev, modality: res.data.modality }));
            setModalityNotes('');
            showToast('Group modality built');
        } catch (err) {
            setError('Could not build group modality.');
        } finally {
            setBuildingModality(false);
        }
    };

    const showToast = (msg) => {
        const el = document.createElement('div');
        el.textContent = msg;
        el.style.cssText = 'position:fixed;bottom:24px;right:24px;background:var(--forest);color:white;padding:12px 20px;border-radius:10px;font-size:13.5px;z-index:9999;font-family:DM Sans,sans-serif;box-shadow:0 4px 16px rgba(0,0,0,0.2)';
        document.body.appendChild(el);
        setTimeout(() => el.remove(), 2500);
    };

    if (loading) return <div style={styles.loading}><LoadingLeaf size={48} label="Loading group…" /></div>;

    if (notFound || !group) {
        return (
            <div style={styles.page}>
                <button style={styles.backLink} onClick={() => navigate('/groups')}>← Back to Groups</button>
                <div style={styles.emptyMain}>
                    <div style={styles.emptyMainIcon}>◎</div>
                    <h3 style={styles.emptyMainTitle}>Group not found</h3>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.page}>
            <button style={styles.backLink} onClick={() => navigate('/groups')}>← Back to Groups</button>

            {error && <div style={styles.errorAlert}>{error}</div>}

            {/* Group header */}
            <div style={styles.groupHeader}>
                {editingGroup ? (
                    <div style={{ flex: 1 }}>
                        <input
                            style={{ ...styles.input, fontSize: '18px', marginBottom: '8px' }}
                            value={editForm.name}
                            onChange={e => setEditForm({ ...editForm, name: e.target.value })}
                        />
                        <textarea
                            style={{ ...styles.input, minHeight: '60px', resize: 'vertical' }}
                            value={editForm.description}
                            onChange={e => setEditForm({ ...editForm, description: e.target.value })}
                            placeholder="Group description..."
                        />
                        <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                            <button style={styles.createBtn} onClick={handleUpdateGroup} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
                            <button style={styles.cancelBtn} onClick={() => setEditingGroup(false)}>Cancel</button>
                        </div>
                    </div>
                ) : (
                    <>
                        <div style={{ flex: 1 }}>
                            <div style={styles.groupName}>{group.name}</div>
                            {group.description && <div style={styles.groupDesc}>{group.description}</div>}
                            <div style={styles.groupMeta}>
                                <span style={styles.groupMetaPill}>{(group.clients || []).length} clients</span>
                                {group.modality_updated_at && (
                                    <span style={styles.groupMetaPill}>Modality built {new Date(group.modality_updated_at).toLocaleDateString()}</span>
                                )}
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                            <button style={styles.editBtn} onClick={() => { setEditingGroup(true); setEditForm({ name: group.name, description: group.description || '' }); }}>Edit</button>
                            <button style={styles.deleteBtn} onClick={handleDeleteGroup}>Delete</button>
                        </div>
                    </>
                )}
            </div>

            {/* Tabs */}
            <div style={styles.tabBar}>
                {[
                    { id: 'overview', label: 'Overview' },
                    { id: 'sessions', label: `Sessions (${groupSessions.length})` },
                    { id: 'modality', label: '◈ Modality' },
                    { id: 'chat', label: `✈️ Chat${groupMessages.length > 0 ? ` (${groupMessages.length})` : ''}` },
                    { id: 'members', label: `Members (${(group.clients || []).length})` },
                ].map(tab => (
                    <button
                        key={tab.id}
                        style={{ ...styles.tab, ...(activeTab === tab.id ? styles.tabActive : {}) }}
                        onClick={() => setActiveTab(tab.id)}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Overview tab */}
            {activeTab === 'overview' && (
                <div style={styles.grid}>
                    <div style={styles.mainCol}>

                        {/* Add session */}
                        {showAddSession ? (
                            <div style={styles.card}>
                                <div style={{ padding: '24px' }}>
                                    <SessionForm
                                        initialClientIds={(group.clients || []).map(c => c.id)}
                                        initialGroupId={group.id}
                                        lockSelection
                                        lockedLabel={`${group.name} · ${(group.clients || []).length} clients`}
                                        embedded
                                        onCancel={() => setShowAddSession(false)}
                                        onSaved={() => { setShowAddSession(false); loadGroup(group.id); }}
                                    />
                                </div>
                            </div>
                        ) : (
                            <div style={styles.card}>
                                <div style={{ padding: '20px 22px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <div>
                                        <div style={styles.cardTitle}>Record a session</div>
                                        <div style={{ fontSize: '12.5px', color: 'var(--text-soft)', marginTop: '2px' }}>
                                            {(group.clients || []).length === 0
                                                ? <>This group has no members yet — <span style={{ color: 'var(--sage)', cursor: 'pointer', fontWeight: 500 }} onClick={() => setActiveTab('members')}>add one first →</span></>
                                                : `Capture content and generate an AI summary for ${group.name}.`}
                                        </div>
                                    </div>
                                    <button
                                        style={styles.buildBtn}
                                        onClick={() => setShowAddSession(true)}
                                        disabled={(group.clients || []).length === 0}
                                    >
                                        + Add session
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Most recent session */}
                        {groupSessions.length > 0 && (
                            <div style={styles.card}>
                                <div style={styles.cardHeader}>
                                    <span style={styles.cardTitle}>Most recent session</span>
                                    <button style={styles.cardAction} onClick={() => setActiveTab('sessions')}>All sessions →</button>
                                </div>
                                <div style={styles.cardBody}>
                                    {(() => {
                                        const s = groupSessions[0];
                                        return (
                                            <div style={styles.sessionRow} onClick={() => navigate(`/sessions?session=${s.id}`)}>
                                                <div style={styles.sessionDate}>{new Date(s.date).toLocaleDateString('default', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
                                                <div style={styles.sessionTitle}>{s.title || 'Untitled session'}</div>
                                                <div style={styles.sessionMeta}>{(s.participant_names || []).length} participants</div>
                                            </div>
                                        );
                                    })()}
                                </div>
                            </div>
                        )}

                        {/* Modality snippet */}
                        <div style={styles.card}>
                            <div style={styles.cardHeader}>
                                <span style={styles.cardTitle}>◈ Group modality</span>
                                <button style={styles.cardAction} onClick={() => setActiveTab('modality')}>
                                    {group.modality?.summary ? 'View full →' : 'Build →'}
                                </button>
                            </div>
                            <div style={styles.cardBody}>
                                {group.modality?.summary ? (
                                    <div style={styles.modalitySummaryText}>{group.modality.summary}</div>
                                ) : (
                                    <p style={styles.emptyText}>Not built yet — synthesize a shared modality from this group's session data.</p>
                                )}
                            </div>
                        </div>
                    </div>

                    <div style={styles.sideCol}>
                        {/* Members */}
                        <div style={styles.card}>
                            <div style={styles.cardHeader}>
                                <span style={styles.cardTitle}>Members</span>
                                <button style={styles.cardAction} onClick={() => setActiveTab('members')}>Manage →</button>
                            </div>
                            <div style={styles.cardBody}>
                                {(group.clients || []).length === 0 ? (
                                    <p style={styles.emptyText}>No members yet.</p>
                                ) : (
                                    <div style={styles.memberList}>
                                        {(group.clients || []).map(client => {
                                            const initials = client.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
                                            return (
                                                <div key={client.id} style={styles.memberRow}>
                                                    <div style={styles.memberAvatar}>{initials}</div>
                                                    <span style={styles.memberName} onClick={() => navigate(`/clients/${client.id}`)}>{client.name}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Telegram chat status */}
                        <div style={styles.card}>
                            <div style={styles.cardHeader}>
                                <span style={styles.cardTitle}>✈️ Telegram</span>
                                <button style={styles.cardAction} onClick={() => setActiveTab('chat')}>
                                    {groupTelegram?.telegram_connected ? 'View →' : 'Connect →'}
                                </button>
                            </div>
                            <div style={styles.cardBody}>
                                <p style={styles.emptyText}>
                                    {groupTelegram?.telegram_connected
                                        ? `Connected · ${groupMessages.length} message${groupMessages.length !== 1 ? 's' : ''} captured`
                                        : 'Not connected yet.'}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Members tab */}
            {activeTab === 'members' && (
                <div style={styles.card}>
                    <div style={styles.cardBody}>
                        {(group.clients || []).length === 0 ? (
                            <p style={styles.emptyText}>No members yet. Add a client below.</p>
                        ) : (
                            <div style={styles.memberList}>
                                {(group.clients || []).map(client => {
                                    const initials = client.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
                                    return (
                                        <div key={client.id} style={styles.memberRow}>
                                            <div style={styles.memberAvatar}>{initials}</div>
                                            <span style={styles.memberName} onClick={() => navigate(`/clients/${client.id}`)}>{client.name}</span>
                                            <button style={styles.removeBtn} onClick={() => removeClient(client.id)}>Remove</button>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                        {(() => {
                            const memberIds = (group.clients || []).map(c => c.id);
                            const nonMembers = clients.filter(c => !memberIds.includes(c.id));
                            if (nonMembers.length === 0) return null;
                            return showAddDropdown ? (
                                <div style={styles.addRow}>
                                    <select style={styles.addSelect} value={addClientId} onChange={e => setAddClientId(e.target.value)} autoFocus>
                                        <option value="">Select a client…</option>
                                        {nonMembers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                    </select>
                                    <button style={styles.createBtn} onClick={handleAddClient} disabled={!addClientId}>Add</button>
                                    <button style={styles.cancelBtn} onClick={() => { setShowAddDropdown(false); setAddClientId(''); }}>Cancel</button>
                                </div>
                            ) : (
                                <button style={styles.addMemberBtn} onClick={() => setShowAddDropdown(true)}>+ Add client</button>
                            );
                        })()}
                    </div>
                </div>
            )}

            {/* Sessions tab */}
            {activeTab === 'sessions' && (
                <div>
                    <div style={styles.card}>
                        <div style={styles.cardHeader}><span style={styles.cardTitle}>Session types</span></div>
                        <div style={styles.cardBody}>
                            <p style={{ fontSize: '12.5px', color: 'var(--text-soft)', marginTop: 0, marginBottom: '14px', lineHeight: 1.5 }}>
                                Sessions that are unique to your group offering go here. If you give 1:1 sessions to people within a group, add this session type below — you can link the client back to the group later.
                            </p>
                            {sessionTypes.length === 0 ? (
                                <p style={styles.emptyText}>No session types yet.</p>
                            ) : (
                                <div style={styles.memberList}>
                                    {sessionTypes.map(st => (
                                        <div key={st.id} style={styles.memberRow}>
                                            <span style={styles.memberName}>{st.name}</span>
                                            <button style={styles.removeBtn} onClick={() => handleRemoveSessionType(st.id)}>Remove</button>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {showAddTypeRow ? (
                                <div style={styles.addRow}>
                                    <input
                                        style={{ ...styles.addSelect, cursor: 'text' }}
                                        value={newTypeName}
                                        onChange={e => setNewTypeName(e.target.value)}
                                        placeholder="e.g. Monthly Coaching Call"
                                        autoFocus
                                        onKeyDown={e => e.key === 'Enter' && handleAddSessionType()}
                                    />
                                    <button style={styles.createBtn} onClick={handleAddSessionType} disabled={!newTypeName.trim()}>Add</button>
                                    <button style={styles.cancelBtn} onClick={() => { setShowAddTypeRow(false); setNewTypeName(''); }}>Cancel</button>
                                </div>
                            ) : (
                                <button style={styles.addMemberBtn} onClick={() => setShowAddTypeRow(true)}>+ Add session type</button>
                            )}
                        </div>
                    </div>
                    {showAddSession ? (
                        <div style={styles.card}>
                            <div style={{ padding: '24px' }}>
                                <SessionForm
                                    initialClientIds={(group.clients || []).map(c => c.id)}
                                    initialGroupId={group.id}
                                    lockSelection
                                    lockedLabel={`${group.name} · ${(group.clients || []).length} clients`}
                                    embedded
                                    onCancel={() => setShowAddSession(false)}
                                    onSaved={() => { setShowAddSession(false); loadGroup(group.id); }}
                                />
                            </div>
                        </div>
                    ) : (
                        <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '12px' }}>
                            {(group.clients || []).length === 0 && (
                                <div style={{ fontSize: '12.5px', color: 'var(--text-soft)' }}>
                                    This group has no members yet — <span style={{ color: 'var(--sage)', cursor: 'pointer', fontWeight: 500 }} onClick={() => setActiveTab('members')}>add one first →</span>
                                </div>
                            )}
                            <button
                                style={styles.buildBtn}
                                onClick={() => setShowAddSession(true)}
                                disabled={(group.clients || []).length === 0}
                            >
                                + Add session
                            </button>
                        </div>
                    )}
                    {!showAddSession && (
                        <div style={styles.card}>
                            <div style={styles.cardBody}>
                                {groupSessions.length === 0 ? (
                                    <p style={styles.emptyText}>No group sessions recorded yet.</p>
                                ) : (
                                    <div style={styles.sessionList}>
                                        {groupSessions.map(session => (
                                            <div key={session.id} style={styles.sessionRow} onClick={() => navigate(`/sessions?session=${session.id}`)}>
                                                <div style={styles.sessionDate}>{new Date(session.date).toLocaleDateString('default', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
                                                <div style={styles.sessionTitle}>{session.title || 'Untitled session'}</div>
                                                <div style={styles.sessionMeta}>{(session.participant_names || []).length} participants</div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Modality tab */}
            {activeTab === 'modality' && (
                <div style={styles.card}>
                    <div style={styles.cardHeader}>
                        <span style={styles.cardTitle}>◈ Group modality</span>
                        <button style={styles.buildBtn} onClick={() => setShowModalityNotesModal(true)} disabled={buildingModality || (group.clients || []).length === 0}>
                            {buildingModality ? 'Building...' : group.modality?.summary ? '↻ Rebuild' : 'Build modality'}
                        </button>
                    </div>
                    <div style={styles.cardBody}>
                        {(group.clients || []).length === 0 && <p style={styles.emptyText}>Add members first.</p>}
                        {(group.clients || []).length > 0 && !group.modality?.summary && !buildingModality && (
                            <div style={styles.buildPrompt}>
                                <div style={styles.buildPromptIcon}>◈</div>
                                <div style={styles.buildPromptText}>Build a shared modality from all {(group.clients || []).length} clients — synthesizing common pain points, breakthroughs, and transformation patterns.</div>
                                <button style={styles.buildPromptBtn} onClick={() => setShowModalityNotesModal(true)}>Build group modality</button>
                            </div>
                        )}
                        {buildingModality && (
                            <div style={styles.buildingState}>
                                <div style={styles.buildingText}>Analyzing sessions across {(group.clients || []).length} clients...</div>
                                <div style={styles.progressBar}><div style={styles.progressFill} /></div>
                            </div>
                        )}
                        {group.modality?.summary && !buildingModality && (
                            <div>
                                <div style={styles.modalitySummary}>
                                    <div style={styles.modalitySummaryLabel}>Group essence</div>
                                    <div style={styles.modalitySummaryText}>{group.modality.summary}</div>
                                </div>
                                {[
                                    { key: 'populationContext', label: 'Population context' },
                                    { key: 'commonThemes', label: 'Common themes' },
                                    { key: 'painPoints', label: 'Pain points' },
                                    { key: 'breakthroughs', label: 'Breakthrough patterns' },
                                    { key: 'transformations', label: 'Transformation arc' },
                                    { key: 'effectiveLanguage', label: 'Effective language' },
                                    { key: 'resistancePatterns', label: 'Resistance patterns' },
                                    { key: 'coachingApproach', label: 'Coaching approach' },
                                ].map(section => group.modality[section.key] && (
                                    <div key={section.key} style={styles.modalitySection}>
                                        <div style={styles.modalitySectionLabel}>{section.label}</div>
                                        <div style={styles.modalitySectionText}>{group.modality[section.key]}</div>
                                    </div>
                                ))}
                                {group.modality.memberHighlights?.length > 0 && (
                                    <div style={styles.modalitySection}>
                                        <div style={styles.modalitySectionLabel}>Member highlights</div>
                                        <div style={{ fontSize: '12px', color: 'var(--text-soft)', marginBottom: '8px' }}>
                                            Individually-attributed moments — not assumed to apply to the whole group.
                                        </div>
                                        {group.modality.memberHighlights.map((h, i) => (
                                            <div key={i} style={{ marginBottom: '8px' }}>
                                                <strong style={{ fontSize: '13.5px', color: 'var(--text-dark)' }}>{h.clientName}: </strong>
                                                <span style={styles.modalitySectionText}>{h.note}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
            {/* Chat tab */}
            {activeTab === 'chat' && <div style={styles.card}>
                <div style={styles.cardHeader}>
                    <span style={styles.cardTitle}>✈️ Telegram Group Chat</span>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        {groupTelegram?.telegram_connected && groupMessages.length > 0 && (
                            <button onClick={handleAnalyzeChat} disabled={analyzingChat} style={styles.buildBtn}>
                                {analyzingChat ? 'Analyzing...' : chatAnalysis ? '↻ Re-analyze' : '◎ Analyze chat'}
                            </button>
                        )}
                        {groupTelegram?.telegram_connected && (
                            <button onClick={handleDisconnectGroupTelegram} style={{ fontSize: 12, color: 'var(--red)', background: 'none', border: 'none', cursor: 'pointer' }}>
                                Unlink
                            </button>
                        )}
                    </div>
                </div>
                <div style={styles.cardBody}>
                    {!groupTelegram?.telegram_connected ? (
                        <div>
                            <p style={{ ...styles.emptyText, marginBottom: 16 }}>
                                Link a Telegram group to capture messages from your clients and use them to inform the group modality and session summaries.
                            </p>
                            <div style={{ fontSize: 13, color: 'var(--text-mid)', marginBottom: 8, fontWeight: 500 }}>Setup steps:</div>
                            <ol style={{ fontSize: 13, color: 'var(--text-mid)', paddingLeft: 18, lineHeight: 2, marginBottom: 16 }}>
                                <li>Add <strong>@{process.env.REACT_APP_TELEGRAM_BOT_USERNAME || 'your bot'}</strong> to your Telegram group</li>
                                <li>Copy the code below</li>
                                <li>Send <code style={{ background: 'var(--mint)', padding: '1px 6px', borderRadius: 4 }}>/linkgroup {groupTelegram?.link_code}</code> in the group</li>
                            </ol>
                            {groupTelegram?.link_code && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <div style={{ ...styles.input, flex: 1, background: 'var(--mint)', fontFamily: 'monospace', fontSize: 15, letterSpacing: 2, color: 'var(--text-dark)' }}>
                                        {groupTelegram.link_code}
                                    </div>
                                    <button style={styles.createBtn} onClick={() => { navigator.clipboard.writeText(groupTelegram.link_code); showToast('Code copied'); }}>
                                        Copy
                                    </button>
                                </div>
                            )}
                        </div>
                    ) : groupMessages.length === 0 ? (
                        <p style={styles.emptyText}>Telegram group linked. Messages will appear here as clients chat.</p>
                    ) : (
                        <>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 320, overflowY: 'auto', marginBottom: chatAnalysis ? 20 : 0 }}>
                                {groupMessages.map(msg => (
                                    <div key={msg.id} style={{ padding: '8px 12px', borderRadius: 8, background: 'var(--cream)' }}>
                                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 3 }}>
                                            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--sage)' }}>{msg.sender_name}</span>
                                            {!msg.client_id && <span style={{ fontSize: 11, color: 'var(--text-faint)', fontStyle: 'italic' }}>not linked</span>}
                                            <span style={{ fontSize: 11, color: 'var(--text-faint)', marginLeft: 'auto' }}>
                                                {new Date(msg.created_at).toLocaleString('default', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                            </span>
                                        </div>
                                        <div style={{ fontSize: 13.5, color: 'var(--text-dark)', lineHeight: 1.5 }}>{msg.content}</div>
                                    </div>
                                ))}
                            </div>

                            {analyzingChat && (
                                <div style={styles.buildingState}>
                                    <div style={styles.buildingText}>Analyzing group chat...</div>
                                    <div style={styles.progressBar}><div style={styles.progressFill} /></div>
                                </div>
                            )}

                            {chatAnalysis && !analyzingChat && (
                                <div style={{ borderTop: '1px solid var(--border-light)', paddingTop: 20 }}>
                                    <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--sage)', marginBottom: 12 }}>◎ Chat Analysis</div>

                                    <div style={{ ...styles.modalitySummary, marginBottom: 16 }}>
                                        <div style={styles.modalitySummaryLabel}>Summary</div>
                                        <div style={styles.modalitySummaryText}>{chatAnalysis.summary}</div>
                                    </div>

                                    {chatAnalysis.themes?.length > 0 && (
                                        <div style={{ marginBottom: 16 }}>
                                            <div style={styles.modalitySectionLabel}>Themes</div>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                                                {chatAnalysis.themes.map((t, i) => (
                                                    <span key={i} style={{ background: 'var(--mint)', color: 'var(--sage)', borderRadius: 20, fontSize: 12, fontWeight: 500, padding: '3px 10px' }}>{t}</span>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {chatAnalysis.group_dynamics && (
                                        <div style={{ ...styles.modalitySection, marginBottom: 16 }}>
                                            <div style={styles.modalitySectionLabel}>Group dynamics</div>
                                            <div style={styles.modalitySectionText}>{chatAnalysis.group_dynamics}</div>
                                        </div>
                                    )}

                                    {chatAnalysis.client_insights?.length > 0 && (
                                        <div style={{ marginBottom: 16 }}>
                                            <div style={{ ...styles.modalitySectionLabel, marginBottom: 10 }}>Per-client insights</div>
                                            {chatAnalysis.client_insights.map((ci, i) => (
                                                <div key={i} style={{ background: 'var(--cream)', borderRadius: 10, padding: '12px 16px', marginBottom: 8 }}>
                                                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-dark)', marginBottom: 8 }}>{ci.name}</div>
                                                    {ci.highlights?.map((h, j) => (
                                                        <div key={j} style={{ fontSize: 13, color: 'var(--text-mid)', lineHeight: 1.6, marginBottom: 4 }}>· {h}</div>
                                                    ))}
                                                    {ci.growth_edge && <div style={{ fontSize: 12, color: 'var(--sage)', marginTop: 6, fontWeight: 500 }}>Growth edge: {ci.growth_edge}</div>}
                                                    {ci.coaching_note && <div style={{ fontSize: 12, color: 'var(--text-soft)', marginTop: 4, fontStyle: 'italic' }}>Coach note: {ci.coaching_note}</div>}
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {chatAnalysis.session_suggestions?.length > 0 && (
                                        <div>
                                            <div style={styles.modalitySectionLabel}>Suggested for next session</div>
                                            {chatAnalysis.session_suggestions.map((s, i) => (
                                                <div key={i} style={{ fontSize: 13, color: 'var(--text-mid)', lineHeight: 1.6, marginTop: 4 }}>→ {s}</div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>}

            {showModalityNotesModal && (
                <div style={styles.modalOverlay} onClick={() => setShowModalityNotesModal(false)}>
                    <div style={styles.modal} onClick={e => e.stopPropagation()}>
                        <div style={styles.modalTitle}>{group.modality?.summary ? 'Rebuild group modality' : 'Build group modality'}</div>
                        <div style={styles.modalSub}>Anything specific you'd like to see different or emphasized this time? Optional — leave blank to let it synthesize freely from session data.</div>
                        <textarea
                            style={{ ...styles.input, minHeight: '110px', resize: 'vertical' }}
                            placeholder="e.g. Focus more on resistance patterns, less on language style. Or: the last version felt too generic — be more specific about what triggers breakthroughs for this group."
                            value={modalityNotes}
                            onChange={e => setModalityNotes(e.target.value)}
                            autoFocus
                        />
                        <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                            <button style={styles.createBtn} onClick={() => handleBuildModality(modalityNotes)}>
                                {group.modality?.summary ? 'Rebuild' : 'Build'}
                            </button>
                            <button style={styles.cancelBtn} onClick={() => { setShowModalityNotesModal(false); setModalityNotes(''); }}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

const styles = {
    page: { padding: '28px 36px', maxWidth: '1100px', fontFamily: "'DM Sans', sans-serif" },
    loading: { padding: '40px', color: 'var(--text-soft)', fontSize: '14px', fontFamily: "'DM Sans', sans-serif" },
    backLink: { background: 'none', border: 'none', color: 'var(--sage)', fontFamily: "'DM Sans', sans-serif", fontSize: '13.5px', fontWeight: '500', cursor: 'pointer', padding: 0, marginBottom: '20px' },
    emptyMain: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px', textAlign: 'center' },
    emptyMainIcon: { fontSize: '40px', color: 'var(--text-faint)', marginBottom: '16px' },
    emptyMainTitle: { fontFamily: "'Lora', Georgia, serif", fontSize: '22px', fontWeight: '400', color: 'var(--text-dark)', marginBottom: '8px' },
    emptyText: { fontSize: '14px', color: 'var(--text-soft)' },
    groupHeader: { display: 'flex', alignItems: 'flex-start', gap: '16px', marginBottom: '24px', background: 'white', borderRadius: '14px', border: '1px solid var(--border)', padding: '24px', boxShadow: 'var(--shadow-sm)' },
    groupName: { fontFamily: "'Lora', Georgia, serif", fontSize: '24px', fontWeight: '400', color: 'var(--text-dark)', marginBottom: '6px' },
    groupDesc: { fontSize: '14px', color: 'var(--text-mid)', lineHeight: 1.6, marginBottom: '10px' },
    groupMeta: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
    groupMetaPill: { background: 'var(--mint)', color: 'var(--sage)', borderRadius: '20px', fontSize: '11.5px', fontWeight: '500', padding: '3px 10px' },
    editBtn: { padding: '8px 14px', borderRadius: '8px', background: 'none', border: '1px solid var(--border)', color: 'var(--text-mid)', fontFamily: "'DM Sans', sans-serif", fontSize: '12.5px', cursor: 'pointer' },
    deleteBtn: { padding: '8px 14px', borderRadius: '8px', background: 'none', border: '1px solid rgba(194,94,94,0.3)', color: 'var(--red)', fontFamily: "'DM Sans', sans-serif", fontSize: '12.5px', cursor: 'pointer' },
    card: { background: 'white', borderRadius: '14px', border: '1px solid var(--border)', marginBottom: '20px', overflow: 'hidden', boxShadow: 'var(--shadow-sm)' },
    cardHeader: { padding: '15px 22px', borderBottom: '1px solid var(--border-light)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
    cardTitle: { fontSize: '13.5px', fontWeight: '600', color: 'var(--text-dark)' },
    cardBody: { padding: '20px 22px' },
    cardAction: { fontSize: '12.5px', color: 'var(--sage)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", fontWeight: '500' },
    grid: { display: 'grid', gridTemplateColumns: '1fr 340px', gap: '20px', alignItems: 'start' },
    mainCol: { display: 'flex', flexDirection: 'column', gap: '20px' },
    sideCol: { display: 'flex', flexDirection: 'column', gap: '20px' },
    memberList: { display: 'flex', flexDirection: 'column', gap: '2px', marginBottom: '14px' },
    memberRow: { display: 'flex', alignItems: 'center', gap: '12px', padding: '9px 12px', borderRadius: '8px', background: 'var(--cream)' },
    memberAvatar: { width: '28px', height: '28px', borderRadius: '50%', background: 'var(--mint)', color: 'var(--sage)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: '600', flexShrink: 0 },
    memberName: { flex: 1, fontSize: '13.5px', fontWeight: '500', color: 'var(--text-dark)', cursor: 'pointer' },
    removeBtn: { padding: '4px 10px', borderRadius: '6px', background: 'none', border: '1px solid var(--border)', color: 'var(--text-soft)', fontFamily: "'DM Sans', sans-serif", fontSize: '12px', cursor: 'pointer' },
    addRow: { display: 'flex', gap: '8px', alignItems: 'center', marginTop: '4px' },
    addSelect: { flex: 1, padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--border)', fontFamily: "'DM Sans', sans-serif", fontSize: '13.5px', color: 'var(--text-dark)', background: 'white', cursor: 'pointer' },
    addMemberBtn: { marginTop: '4px', padding: '8px 14px', borderRadius: '8px', background: 'none', border: '1px dashed var(--border)', color: 'var(--sage)', fontFamily: "'DM Sans', sans-serif", fontSize: '13px', cursor: 'pointer', fontWeight: '500' },
    sessionList: { display: 'flex', flexDirection: 'column', gap: '2px' },
    sessionRow: { display: 'flex', alignItems: 'center', gap: '16px', padding: '10px 12px', borderRadius: '8px', cursor: 'pointer', transition: 'background 0.15s' },
    sessionDate: { fontSize: '12px', color: 'var(--text-soft)', minWidth: '100px', flexShrink: 0 },
    sessionTitle: { flex: 1, fontSize: '13.5px', fontWeight: '500', color: 'var(--text-dark)' },
    sessionMeta: { fontSize: '12px', color: 'var(--text-faint)', flexShrink: 0 },
    buildBtn: { padding: '7px 14px', borderRadius: '8px', background: 'var(--sage)', border: 'none', color: 'white', fontFamily: "'DM Sans', sans-serif", fontSize: '12.5px', cursor: 'pointer', fontWeight: '500' },
    buildPrompt: { textAlign: 'center', padding: '32px 16px' },
    buildPromptIcon: { fontSize: '28px', color: 'var(--sage)', marginBottom: '12px' },
    buildPromptText: { fontSize: '14px', color: 'var(--text-mid)', lineHeight: 1.6, marginBottom: '20px', maxWidth: '440px', margin: '0 auto 20px' },
    buildPromptBtn: { padding: '10px 22px', borderRadius: '8px', background: 'var(--sage)', border: 'none', color: 'white', fontFamily: "'DM Sans', sans-serif", fontSize: '14px', fontWeight: '500', cursor: 'pointer' },
    buildingState: { textAlign: 'center', padding: '24px' },
    buildingText: { fontSize: '14px', color: 'var(--text-soft)', marginBottom: '12px' },
    progressBar: { height: '3px', background: 'var(--border)', borderRadius: '2px', overflow: 'hidden', margin: '10px auto', maxWidth: '300px' },
    progressFill: { height: '100%', background: 'linear-gradient(90deg, var(--sage), var(--gold))', borderRadius: '2px', width: '100%', animation: 'pulse 1.5s ease-in-out infinite' },
    modalitySummary: { background: 'var(--mint)', borderRadius: '10px', padding: '16px 20px', marginBottom: '20px' },
    modalitySummaryLabel: { fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--sage)', marginBottom: '8px' },
    modalitySummaryText: { fontSize: '14px', color: 'var(--text-dark)', lineHeight: 1.7, fontStyle: 'italic' },
    modalitySection: { padding: '14px 0', borderBottom: '1px solid var(--border-light)' },
    modalitySectionLabel: { fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-soft)', marginBottom: '6px' },
    modalitySectionText: { fontSize: '14px', color: 'var(--text-dark)', lineHeight: 1.7 },
    tabBar: { display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid var(--border)', paddingBottom: 0 },
    tab: { padding: '8px 16px', borderRadius: '8px 8px 0 0', border: '1px solid transparent', borderBottom: 'none', background: 'none', fontFamily: "'DM Sans', sans-serif", fontSize: '13px', color: 'var(--text-soft)', cursor: 'pointer', marginBottom: -1 },
    tabActive: { background: 'white', border: '1px solid var(--border)', borderBottom: '1px solid white', color: 'var(--text-dark)', fontWeight: 600 },
    input: { width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--cream)', fontFamily: "'DM Sans', sans-serif", fontSize: '14px', color: 'var(--text-dark)', outline: 'none', boxSizing: 'border-box' },
    createBtn: { padding: '9px 18px', borderRadius: '8px', background: 'var(--sage)', border: 'none', color: 'white', fontFamily: "'DM Sans', sans-serif", fontSize: '13px', cursor: 'pointer', fontWeight: '500' },
    cancelBtn: { padding: '9px 16px', borderRadius: '8px', background: 'none', border: '1px solid var(--border)', color: 'var(--text-mid)', fontFamily: "'DM Sans', sans-serif", fontSize: '13px', cursor: 'pointer' },
    errorAlert: { background: 'var(--red-light)', color: 'var(--red)', padding: '12px 16px', fontSize: '13px', marginBottom: '16px', borderRadius: '8px' },
    modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(26,46,40,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, backdropFilter: 'blur(2px)' },
    modal: { background: 'white', borderRadius: '16px', padding: '28px', width: '480px', maxWidth: '95vw', boxShadow: 'var(--shadow-lg)' },
    modalTitle: { fontFamily: "'Lora', Georgia, serif", fontSize: '20px', fontWeight: '400', color: 'var(--text-dark)', marginBottom: '8px' },
    modalSub: { fontSize: '13px', color: 'var(--text-soft)', lineHeight: 1.5, marginBottom: '16px' },
};
